/**
 * 主檔 · 貨品面板（`ui-spec.md` 頁 15 下半）—— 列表（篩選／cbm／相片）＋ 新增表單
 *
 * ⚠️ cbm 只**顯示**後端回嘅值（唯一公式喺 `@coeldery/sku-schema`）。
 * ⚠️ 相片：`POST /api/items/:id/photo`（**原始 bytes，非 multipart**）；
 *    讀取經已登入 session 串流（R2 私密，冇公開 URL）。
 */
import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '@coeldery/empty-state';
import FormSelectField from '@coeldery/form-select-field';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import ItemCreateForm from './ItemCreateForm';
import { ApiError, api, type Item, type Supplier } from '../lib/api';

export interface ItemAdminPanelProps {
  suppliers: Supplier[];
  items: Item[];
  filter: string;
  onFilterChange: (supplierId: string) => void;
  onChanged: () => Promise<void>;
}

export default function ItemAdminPanel({
  suppliers,
  items,
  filter,
  onFilterChange,
  onChanged,
}: ItemAdminPanelProps) {
  const { t } = useTranslation();
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filterOptions = [
    { value: '', label: t('master.allSuppliers') },
    ...suppliers.map((s) => ({ value: s.id, label: `${s.code} ${s.name}` })),
  ];

  const onPickPhoto = async (itemId: string, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 清空 → 同一張相可以再揀（重試）
    if (!file) return;
    setUploadingId(itemId);
    setError(null);
    try {
      await api.uploadItemPhoto(itemId, file);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <section style={{ ...card, padding: '16px' }}>
      <h2 style={{ margin: '0 0 10px', fontSize: '17px', fontWeight: 800 }}>
        {t('master.itemsTitle')}
      </h2>

      <div style={{ marginBottom: '12px' }}>
        <FormSelectField
          id="f-item-filter"
          labelKey="master.filterSupplier"
          value={filter}
          onChange={onFilterChange}
          options={filterOptions}
        />
      </div>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}

      {items.length === 0 ? (
        <EmptyState titleKey="master.emptyItemsTitle" bodyKey="master.emptyItemsBody" />
      ) : (
        <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
          {items.map((it) => (
            <li
              key={it.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid var(--color-divider)',
              }}
            >
              {it.hasPhoto ? (
                <img
                  src={api.itemPhotoUrl(it.id)}
                  alt={t('master.photo')}
                  width={56}
                  height={56}
                  style={{ objectFit: 'cover', borderRadius: '10px', flex: '0 0 auto' }}
                />
              ) : (
                <span style={{ ...hintStyle, flex: '0 0 auto' }}>{t('master.noPhoto')}</span>
              )}
              <span style={{ flex: '1 1 11rem' }}>
                <strong>{it.name}</strong>
                <br />
                <span style={hintStyle}>
                  {it.barcode} · {it.unitsPerCarton} {t('master.unitsPerCarton')} · {it.cartonLCm}×
                  {it.cartonWCm}×{it.cartonHCm} cm
                </span>
                <br />
                <span style={hintStyle}>
                  {t('master.cbm')} <strong>{it.cbmPerCarton}</strong> ／ {t('master.cbmPerUnit')}{' '}
                  {it.perUnitCbm}
                </span>
              </span>
              <label style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center' }}>
                {uploadingId === it.id ? t('master.photoUploading') : t('master.uploadPhoto')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  hidden
                  onChange={(e) => void onPickPhoto(it.id, e)}
                />
              </label>
            </li>
          ))}
        </ul>
      )}

      <ItemCreateForm suppliers={suppliers} onCreated={onChanged} />
    </section>
  );
}
